import { DashboardSummary } from '@/types/dashboard';
import { FilterOptions, SelectedFilters } from '@/types/filters';
import axiosInstance from '../utils/axios';

/**
 * Performance API Caching Mechanism
 * ----------------------------------
 * This file implements a caching mechanism to improve performance for frequently called APIs.
 * 
 * Key features:
 * 1. Cached data is stored with a timestamp and the params string used for the request
 * 2. Cache validity is checked using the isValidCache function which compares:
 *    - If the cache exists for the endpoint
 *    - If the cache timestamp is recent (within CACHE_DURATION)
 *    - If the params match the current request
 * 3. Cache is updated using the updateCache function
 * 4. All caches can be cleared using clearPerformanceCache
 * 
 * Performance benefits:
 * - Prevents duplicate API calls for the same data within a short time period
 * - Significantly reduces network traffic and server load
 * - Improves UI responsiveness by serving cached data instantly
 * 
 * To manually clear caches:
 * - Use clearFilterOptionsCache() for filter options
 * - Use clearPerformanceCache() for all performance data
 */

const API_BASE_URL = 'https://renewals-insuremind-backend.neuralkart.com/api';

// Use the global axiosInstance instead of creating a new one
const api = axiosInstance;

// Set the base URL for API calls
api.defaults.baseURL = API_BASE_URL;

// Cache for filter options to prevent multiple API calls
let cachedFilterOptions: FilterOptions | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// Cache for performance APIs
interface PerformanceAPICache {
  data: any;
  timestamp: number;
  params: string; // Store URL params to match requests with same filters
}

const performanceCache: Record<string, PerformanceAPICache | null> = {
  'overall': null,
  'lob': null,
  'regionMap': null,
  'productChannel': null,
  'channelProduct': null,
  'productStatus': null,
  'channelStatus': null,
  'subchannelStatus': null,
  'productConversion': null,
  'channelConversion': null,
  'subchannelConversion': null,
  'channelBreakdown': null,
  'managerBreakdown': null,
};

// Helper function to check if cache is valid for a specific endpoint and params
const isValidCache = (endpoint: keyof typeof performanceCache, params: string): boolean => {
  const cache = performanceCache[endpoint];
  if (!cache) return false;
  
  const now = Date.now();
  return !!cache.timestamp && (now - cache.timestamp < CACHE_DURATION) && cache.params === params;
};

// Helper function to update cache for a specific endpoint
const updateCache = (endpoint: keyof typeof performanceCache, data: any, params: string): void => {
  performanceCache[endpoint] = {
    data,
    timestamp: Date.now(),
    params,
  };
};

// Function to clear all performance caches
export const clearPerformanceCache = (): void => {
  Object.keys(performanceCache).forEach(key => {
    performanceCache[key as keyof typeof performanceCache] = null;
  });
};

// No need to add request interceptor here as it's already in the axiosInstance

export interface DrillDownPolicy {
  id: number;
  policyNumber: string;
  product: string;
  channel: string;
  status: string;
  manager: {
    rm: string;
    zm: string;
    nsm: string;
  };
  agent: {
    code: string;
    name: string;
  };
  updateDate: string;
}

export interface BusinessHeadSummary {
  business_head: string;
  date_range: {
    start: string | null;
    end: string | null;
  };
  metrics: {
    [key: string]: {
      ftm: {
        total_base: number;
        conversion: number;
        conv_rate: number;
      };
      aod: {
        total_base: number;
        conversion: number;
        conv_rate: number;
      };
      rnyear_metrics: {
        [key: string]: {
          ftm: {
            total_base: number;
            conversion: number;
            conv_rate: number;
          };
          aod: {
            total_base: number;
            conversion: number;
            conv_rate: number;
          };
        };
      };
    };
  };
}

export interface Metrics {
  ftm: {
    total_base: number;
    conversion: number;
    conv_rate: number;
  };
  aod: {
    total_base: number;
    conversion: number;
    conv_rate: number;
  };
}

export interface RegionHierarchyFilters {
  startDate?: string;
  endDate?: string;
  level?: string;
  parent_id?: string;
  hierarchy_path?: string;
  selected_months?: number[];
  selected_years?: number[];
  selected_lob?: string[];
  selected_products?: (string | ProductInfo)[];
  selected_sub_products?: (string | ProductInfo)[];
  selected_channels?: string[];
  selected_sub_channels?: string[];
}

export interface RegionManagerResponse {
  managers: ManagerData[];
  total_metrics: {
    ftm: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
    aod: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
  };
}

export interface ManagerData {
  id: string;
  name: string;
  role: string;
  channel: string | null;
  state: string | null;
  metrics: Metrics;
  hierarchy_path?: string;
}

export interface AgencyDetails {
  channels: {
    [key: string]: {
      name: string;
      metrics: Metrics;
      subchannels: {
        [key: string]: {
          name: string;
          metrics: Metrics;
          agencies: {
            [key: string]: {
              name: string;
              imd_code: string;
              sol_id: string;
              metrics: Metrics;
            };
          };
        };
      };
    };
  };
  hierarchy: {
    fls: { id: string; name: string; };
    sm: { id: string; name: string; };
    sh: { id: string; name: string; };
    am: { id: string; name: string; };
    rm: { id: string; name: string; };
    zm: { id: string; name: string; };
    nsm: { id: string; name: string; };
    vh: { id: string; name: string; };
    bh: { id: string; name: string; };
  };
  location: {
    partner_region: string;
    partner_zone: string;
    chola_branch: string;
    chola_cluster: string;
    chola_state: string;
  };
}

export interface ProductHierarchy {
  name: string;
  mis_product?: string;
  metrics: {
    ftm: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
    aod: {
      total_base: number;
      conversion: number;
      conv_rate: number;
    };
  };
  products?: {
    [key: string]: {
      name: string;
      mis_product: string;
      metrics: {
        ftm: {
          total_base: number;
          conversion: number;
          conv_rate: number;
        };
        aod: {
          total_base: number;
          conversion: number;
          conv_rate: number;
        };
      };
      rnyears: {
        [key: string]: {
          name: string;
          metrics: {
            ftm: {
              total_base: number;
              conversion: number;
              conv_rate: number;
            };
            aod: {
              total_base: number;
              conversion: number;
              conv_rate: number;
            };
          };
        };
      };
    };
  };
}

export interface ProductStatusMatrix {
  products: string[];
  metrics: {
    [key: string]: {
      status_leads: number;
      status_converted: number;
      total_leads: number;
      total_converted: number;
      status_percentage: number;
      status_conversion_rate: number;
      total_conversion_rate: number;
    };
  };
  selected_status: string;
}

export interface ChannelStatusMatrix {
  channels: string[];
  metrics: {
    [key: string]: {
      status_leads: number;
      status_converted: number;
      total_leads: number;
      total_converted: number;
      status_percentage: number;
      status_conversion_rate: number;
      total_conversion_rate: number;
    };
  };
  selected_status: string;
}

export interface SubChannelStatusMatrix {
  subChannels: string[];
  metrics: {
    [key: string]: {
      status_leads: number;
      status_converted: number;
      total_leads: number;
      total_converted: number;
      status_percentage: number;
      status_conversion_rate: number;
      total_conversion_rate: number;
    };
  };
  selected_status: string;
  parent_channel: string;
}

export interface FilterMapping {
  lob_to_product: Record<string, string[]>;
  product_to_channel: Record<string, string[]>;
  channel_to_subchannel: Record<string, string[]>;
}

export interface ProductInfo {
  product_name: string;
  mis_product: string;
  display_name: string;
}

export interface DashboardFilterOptions {
  channel: string[];
  lob: string[];
  mis_rnyears: string[];
  months: string[];
  product_group: string[];
  sub_product: string[];
  subchannel: string[];
  years: string[];
}

export const fetchFilterOptions = async (): Promise<FilterOptions> => {
  // Check if we have valid cached data
  const now = Date.now();
  if (cachedFilterOptions && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
    return cachedFilterOptions;
  }

  try {
    const response = await api.get('/dashboard/filters');
    
    // Cache the response
    cachedFilterOptions = {
      months: response.data.months || [],
      years: response.data.years || [],
      lob: response.data.lob || [],
      product_group: response.data.product_group || [],
      channel: response.data.channel || [],
      subchannel: response.data.subchannel || [],
      sub_product: response.data.sub_product || [],
    };
    cacheTimestamp = now;
    
    return cachedFilterOptions;
  } catch (error) {
    console.error('Error fetching filter options:', error);
    throw error;
  }
};

// Function to manually clear the cache if needed
export const clearFilterOptionsCache = () => {
  cachedFilterOptions = null;
  cacheTimestamp = null;
};

/**
 * Clears all caches after data uploads that modify the underlying data
 * This should be called after uploads that could affect the dashboard data:
 * - Lead data uploads
 * - Hierarchy data uploads
 * - Product uploads
 * - Renewal target uploads
 * - Lead status updates
 */
export const clearCachesAfterUpload = () => {
  // Clear filter options cache
  clearFilterOptionsCache();
  
  // Clear performance caches
  clearPerformanceCache();
  
};

// Helper function to convert SelectedFilters to RegionHierarchyFilters format
export const convertToRegionHierarchyFilters = (filters: SelectedFilters): RegionHierarchyFilters => {
  return {
    selected_months: filters.selectedMonth ? [parseInt(filters.selectedMonth, 10)] : undefined,
    selected_years: filters.selectedYear ? [parseInt(filters.selectedYear, 10)] : undefined,
    selected_lob: filters.selectedLOB.length > 0 ? filters.selectedLOB : undefined,
    selected_products: filters.selectedProducts.length > 0 ? filters.selectedProducts : undefined,
    selected_sub_products: filters.selectedSubProducts.length > 0 ? filters.selectedSubProducts : undefined,
    selected_channels: filters.selectedChannels.length > 0 ? filters.selectedChannels : undefined,
    selected_sub_channels: filters.selectedSubChannels.length > 0 ? filters.selectedSubChannels : undefined,
  };
};

export const fetchDashboardSummary = async (filters?: SelectedFilters, region?: string): Promise<DashboardSummary> => {
  // Only include filters that have been selected
  const payload: Record<string, any> = {};

  if (filters?.selectedMonth) {
    payload.selected_months = [filters.selectedMonth];
  }
  
  if (filters?.selectedYear) {
    // Find the year option to determine which calendar year to use
    // Use cached options first without triggering a new API call
    const options = cachedFilterOptions || await fetchFilterOptions();
    const yearOption = options.years.find(y => y.id === filters.selectedYear);
    
    if (yearOption && filters.selectedMonth) {
      const selectedMonth = parseInt(filters.selectedMonth);
      // Months 1-3 (Jan-Mar) use end_year, months 4-12 (Apr-Dec) use start_year
      const calendarYear = selectedMonth >= 1 && selectedMonth <= 3 
        ? yearOption.end_year.toString()
        : yearOption.start_year.toString();
      
      payload.selected_years = [calendarYear];
    } else {
      // If month is not selected or year option not found, use financial year ID
      payload.selected_years = [filters.selectedYear];
    }
  }
  
  if (filters?.selectedLOB?.length) {
    payload.selected_lob = filters.selectedLOB;
  }
  
  if (filters?.selectedProducts?.length) {
    const isProductInfo = (p: any): p is ProductInfo => p && typeof p === 'object' && 'mis_product' in p;
    // Convert ProductInfo objects to their mis_product values
    payload.selected_products = (filters.selectedProducts as any[]).map(p => isProductInfo(p) ? p.mis_product : p);
  }
  
  if (filters?.selectedChannels?.length) {
    payload.selected_channels = filters.selectedChannels;
  }

  if (filters?.selectedSubChannels?.length) {
    payload.selected_sub_channels = filters.selectedSubChannels;
  }

  if (region) {
    payload.selected_region = region;
  }

  // Request trend data comparison with previous period
  payload.include_trends = true;

  const response = await api.post('/dashboard/summary', payload);
  
  // If the backend doesn't yet support trend data, we can add mock trend data here temporarily
  // This should be removed once the backend properly implements trend data
  if (!response.data.trends) {
    response.data.trends = {
      totalLeads: 12.5,
      convertedLeads: 8.2,
      conversionRate: -2.1
    };
  }
  
  // Log a warning if subChannelStatusBreakdown is missing from the backend response
  if (!response.data.subChannelStatusBreakdown) {
    console.warn('Backend API does not provide subChannelStatusBreakdown data. This feature requires backend implementation.');
  }
  
  return response.data;
};

// Propensity Predictions API
export interface PropensityFactor {
  feature: string;
  impact: number;
  impact_percentage: number;
  display_name: string;
  formatted_impact: string;
}

export interface PropensityPredictionItem {
  id: number;
  policy_id: string;
  customer_id: string;
  agent_code: string;
  verticle?: string;
  renewal_probability: string;
  predicted_renewal: number;
  top_positive_factors: PropensityFactor[];
  top_negative_factors: PropensityFactor[];
  created_at: string;
  updated_at: string;
  chola_state?: string;
  state_code?: string;
  channel?: string;
  sub_channel?: string;
  business_head?: string;
  vertical_head?: string;
  nsm_emp_name?: string;
  zm_emp_name?: string;
  rm_emp_name?: string;
  am_emp_name?: string;
  state_head?: string;
  sm_emp_name?: string;
  fls_emp_name?: string;
}

export interface GetPropensityPredictionsParams {
  limit?: number;
  offset?: number;
  search?: string; // by policy_id or customer_id
}

export interface PropensityPredictionsResponse {
  total: number;
  limit: number;
  offset: number;
  data: PropensityPredictionItem[];
}

export const getPropensityPredictions = async (
  params: GetPropensityPredictionsParams = {}
): Promise<PropensityPredictionsResponse> => {
  const query = new URLSearchParams();
  if (typeof params.limit === 'number') query.append('limit', String(params.limit));
  if (typeof params.offset === 'number') query.append('offset', String(params.offset));
  if (params.search) query.append('search', params.search);

  const url = `/propensity/propensity-predictions${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data as PropensityPredictionsResponse;
};

// Propensity Manager Hierarchy Breakdown API
export interface PropensityHierarchyPolicyItem {
  policy_id: string;
  customer_id: string;
  agent_code: string;
  verticle?: string;
  renewal_probability: string;
  predicted_renewal: number;
  risk_level?: string;
  premium_amount?: number;
  channel?: string;
  sub_channel?: string;
}

export interface PropensityHierarchyNode {
  employee_id: string;
  employee_name: string;
  employee_role: string; // e.g., SM, FLS, etc.
  total_policies: number;
  total_predicted_renewals: number;
  predicted_renewal_rate: string; // percentage string like "60.0"
  average_renewal_probability: string; // e.g., "0.6136"
  high_risk_count: number;
  medium_risk_count: number;
  low_risk_count: number;
  subordinates?: Array<{
    employee_id: string;
    employee_name: string;
    role?: string; // sometimes present as lowercase (e.g., fls)
    employee_role: string;
    total_policies: number;
    total_predicted_renewals: number;
    predicted_renewal_rate: string;
    average_renewal_probability: string;
    high_risk_count: number;
    medium_risk_count: number;
    low_risk_count: number;
  }>;
  policies?: PropensityHierarchyPolicyItem[];
}

export interface GetPropensityHierarchyParams {
  target_employee_id?: string;
  target_role?: string; // backend expects e.g., fls, sm, etc.
  hierarchy_path?: string;
}

export const fetchPropensityHierarchyBreakdown = async (
  params: GetPropensityHierarchyParams = {}
): Promise<PropensityHierarchyNode> => {
  const query = new URLSearchParams();
  if (params.target_employee_id) query.append('target_employee_id', params.target_employee_id);
  if (params.target_role) query.append('target_role', params.target_role);
  if (params.hierarchy_path) query.append('hierarchy_path', params.hierarchy_path);

  const url = `/propensity/propensity-predictions/hierarchy-breakdown${query.toString() ? `?${query.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data as PropensityHierarchyNode;
};

export const fetchRegionDrillDown = async (stateCode: string): Promise<DrillDownPolicy[]> => {
  try {
    const url = `/dashboard/drill-down/${encodeURIComponent(stateCode)}`;
    
    const response = await api.get(url);
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const fetchProductChannelMatrix = async (startDate?: string, endDate?: string, selectedRegion?: string) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);

    const response = await api.get(`/dashboard/product-channel-matrix${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product-channel matrix:', error);
    throw error;
  }
};

export const fetchBusinessHeadSummary = async (
  businessHead: string,
  startDate?: string,
  endDate?: string,
  filters?: {
    selectedMonths?: number[];
    selectedYears?: number[];
    selectedLOB?: string[];
    selectedProducts?: (string | ProductInfo)[];
    selectedChannels?: string[];
    selectedSubChannels?: string[];
  }
): Promise<BusinessHeadSummary> => {
  try {
    const params = new URLSearchParams();
    params.append('business_head', businessHead);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    // Add additional filters
    if (filters?.selectedMonths?.length) {
      filters.selectedMonths.forEach(month => params.append('selected_months', month.toString()));
    }
    if (filters?.selectedYears?.length) {
      filters.selectedYears.forEach(year => params.append('selected_years', year.toString()));
    }
    if (filters?.selectedLOB?.length) {
      filters.selectedLOB.forEach(lob => params.append('selected_lob', lob));
    }
    if (filters?.selectedProducts?.length) {
      filters.selectedProducts.forEach(product => {
        // If product is a ProductInfo object, use its mis_product property
        const productValue = typeof product === 'string' ? product : product.mis_product;
        params.append('selected_products', productValue);
      });
    }
    if (filters?.selectedChannels?.length) {
      filters.selectedChannels.forEach(channel => params.append('selected_channels', channel));
    }
    if (filters?.selectedSubChannels?.length) {
      filters.selectedSubChannels.forEach(subChannel => params.append('selected_sub_channels', subChannel));
    }

    const response = await api.get(`/dashboard/business-head-summary?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching business head summary:', error);
    throw error;
  }
};

export const fetchBusinessHeads = async (): Promise<string[]> => {
  try {
    const response = await api.get('/dashboard/business-heads');
    return response.data;
  } catch (error) {
    console.error('Error fetching business heads:', error);
    throw error;
  }
};

export const fetchRegionHierarchySummary = async (
  region: string,
  filters?: RegionHierarchyFilters
): Promise<RegionManagerResponse> => {
  try {
    const params = new URLSearchParams();
    
    if (filters?.level) params.append('level', filters.level);
    if (filters?.parent_id) params.append('parent_id', filters.parent_id);
    if (filters?.hierarchy_path) params.append('hierarchy_path', filters.hierarchy_path);
    if (filters?.selected_months) filters.selected_months.forEach(month => params.append('selected_months', month.toString()));
    if (filters?.selected_years) filters.selected_years.forEach(year => params.append('selected_years', year.toString()));
    if (filters?.selected_lob) filters.selected_lob.forEach(lob => params.append('selected_lob', lob));
    if (filters?.selected_products) {
      filters.selected_products.forEach(product => {
        const productValue = typeof product === 'string' ? product : product.mis_product;
        params.append('selected_products', productValue);
      });
    }
    if (filters?.selected_channels) filters.selected_channels.forEach(channel => params.append('selected_channels', channel));
    if (filters?.selected_sub_channels) filters.selected_sub_channels.forEach(subChannel => params.append('selected_sub_channels', subChannel));

    const response = await api.get(`/dashboard/region-hierarchy/${region}/summary${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching region hierarchy summary:', error);
    throw error;
  }
};

export const fetchManagerAgencyDetails = async (
  region: string,
  managerId: string,
  filters?: { 
    selected_channels?: string[];
    selected_sub_channels?: string[];
    hierarchy_path?: string;
    selected_months?: number[];
    selected_years?: number[];
    selected_lob?: string[];
    selected_products?: (string | ProductInfo)[];
  }
): Promise<AgencyDetails> => {
  try {
    if (region && region !== 'all') {
      // Use GET endpoint for specific region
      const params = new URLSearchParams();
      if (filters?.selected_channels) {
        filters.selected_channels.forEach(channel => params.append('selected_channels', channel));
      }
      if (filters?.selected_sub_channels) {
        filters.selected_sub_channels.forEach(subChannel => params.append('selected_sub_channels', subChannel));
      }
      if (filters?.hierarchy_path) {
        params.append('hierarchy_path', filters.hierarchy_path);
      }
      if (filters?.selected_months) {
        filters.selected_months.forEach(month => params.append('selected_months', month.toString()));
      }
      if (filters?.selected_years) {
        filters.selected_years.forEach(year => params.append('selected_years', year.toString()));
      }
      if (filters?.selected_lob) {
        filters.selected_lob.forEach(lob => params.append('selected_lob', lob));
      }
      if (filters?.selected_products) {
        filters.selected_products.forEach(product => {
          const productValue = typeof product === 'string' ? product : product.mis_product;
          params.append('selected_products', productValue);
        });
      }

      const response = await api.get(
        `/dashboard/region-hierarchy/${region}/manager/${managerId}/agency${params.toString() ? `?${params.toString()}` : ''}`
      );
      return response.data;
    } else {
      // Use POST endpoint for all regions or when region is 'all'
      const payload: Record<string, any> = {
        manager_id: managerId
      };
      
      if (filters?.hierarchy_path) payload.hierarchy_path = filters.hierarchy_path;
      if (filters?.selected_months) payload.selected_months = filters.selected_months;
      if (filters?.selected_years) payload.selected_years = filters.selected_years;
      if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
      if (filters?.selected_products) {
        // Convert ProductInfo objects to their mis_product values
        payload.selected_products = filters.selected_products.map(product => 
          typeof product === 'string' ? product : product.mis_product
        );
      }
      if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
      if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
      
      // Log the payload for debugging
      
      const response = await api.post(`/dashboard/region-hierarchy/manager/${managerId}/agency`, payload);
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching manager agency details:', error);
    throw error;
  }
};

export interface ManagerProductHierarchyFilters {
  selected_months?: number[];
  selected_years?: number[];
  selected_lob?: string[];
  selected_products?: (string | ProductInfo)[];
  selected_channels?: string[];
  selected_sub_channels?: string[];
  hierarchy_path?: string;
}

export const fetchManagerProductHierarchy = async (
  region: string,
  managerId: string,
  startDate?: string,
  endDate?: string,
  parentId?: string,
  filters?: ManagerProductHierarchyFilters
): Promise<{ [key: string]: ProductHierarchy }> => {
  try {
    if (region && region !== 'all') {
      // Use GET endpoint for specific region
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (parentId) params.append('parent_id', parentId);
      if (filters?.hierarchy_path) params.append('hierarchy_path', filters.hierarchy_path);
      if (filters?.selected_months) filters.selected_months.forEach(month => params.append('selected_months', month.toString()));
      if (filters?.selected_years) filters.selected_years.forEach(year => params.append('selected_years', year.toString()));
      if (filters?.selected_lob) filters.selected_lob.forEach(lob => params.append('selected_lob', lob));
      if (filters?.selected_products) {
        filters.selected_products.forEach(product => {
          const productValue = typeof product === 'string' ? product : product.mis_product;
          params.append('selected_products', productValue);
        });
      }
      if (filters?.selected_channels) filters.selected_channels.forEach(channel => params.append('selected_channels', channel));
      if (filters?.selected_sub_channels) filters.selected_sub_channels.forEach(subChannel => params.append('selected_sub_channels', subChannel));

      const response = await api.get(`/dashboard/region-hierarchy/${region}/manager/${managerId}/products${params.toString() ? `?${params.toString()}` : ''}`);
      return response.data;
    } else {
      // Use POST endpoint for all regions or when region is 'all'
      const payload: Record<string, any> = {
        manager_id: managerId
      };
      
      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate;
      if (parentId) payload.parent_id = parentId;
      if (filters?.hierarchy_path) payload.hierarchy_path = filters.hierarchy_path;
      if (filters?.selected_months) payload.selected_months = filters.selected_months;
      if (filters?.selected_years) payload.selected_years = filters.selected_years;
      if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
      if (filters?.selected_products) {
        // Convert ProductInfo objects to their mis_product values
        payload.selected_products = filters.selected_products.map(product => 
          typeof product === 'string' ? product : product.mis_product
        );
      }
      if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
      // Ensure selected_sub_channels is included in the payload
      if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
      
      
      
      const response = await api.post(`/dashboard/region-hierarchy/manager/${managerId}/products`, payload);
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching manager product hierarchy:', error);
    throw error;
  }
};

export const fetchProductStatusMatrix = async (
  status: string,
  product: string,
  startDate?: string,
  endDate?: string,
  selectedRegion?: string
): Promise<ProductStatusMatrix> => {
  try {
    const params = new URLSearchParams();
    params.append('status', status);
    params.append('product', product);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);

    
    const response = await api.get(`/dashboard/product-status-matrix?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product-status matrix:', error);
    throw error;
  }
};

export const fetchChannelPerformanceMatrix = async (channel: string, startDate?: string, endDate?: string, selectedRegion?: string) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);
    if (channel) params.append('channel', channel);

    
    const response = await api.get(`/dashboard/channel-performance-matrix${params.toString() ? `?${params.toString()}` : ''}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching channel performance matrix:', error);
    throw error;
  }
};

export const fetchChannelStatusMatrix = async (
  status: string,
  channel: string,
  startDate?: string,
  endDate?: string,
  selectedRegion?: string
): Promise<ChannelStatusMatrix> => {
  try {
    const params = new URLSearchParams();
    params.append('status', status);
    params.append('channel', channel);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (selectedRegion && selectedRegion !== 'all') params.append('selected_region', selectedRegion);

    
    const response = await api.get(`/dashboard/channel-status-matrix?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching channel-status matrix:', error);
    throw error;
  }
};

export const fetchSubchannelStatusDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<SubchannelStatusResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('subchannelStatus', paramsString)) {
      return performanceCache['subchannelStatus']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/subchannel-status?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('subchannelStatus', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching subchannel status distribution:', error);
    throw error;
  }
};

export interface ProductHierarchyFilters {
  selected_months?: number[];
  selected_years?: number[];
  selected_lob?: string[];
  selected_products?: (string | ProductInfo)[];
  selected_channels?: string[];
  selected_sub_channels?: string[];
  selected_region?: string;
}

export const fetchAllProductsHierarchy = async (
  filters?: ProductHierarchyFilters,
  region?: string
): Promise<{ [key: string]: ProductHierarchy }> => {
  try {
    const payload: Record<string, any> = {};
    
    if (filters?.selected_months) payload.selected_months = filters.selected_months;
    if (filters?.selected_years) payload.selected_years = filters.selected_years;
    if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
    if (filters?.selected_products) {
      // Convert ProductInfo objects to their mis_product values
      payload.selected_products = filters.selected_products.map(product => 
        typeof product === 'string' ? product : product.mis_product
      );
    }
    if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
    if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
    if (filters?.selected_region) payload.selected_region = filters.selected_region;
    
    // Also accept region as a separate parameter for backward compatibility
    if (region && region !== 'all') payload.selected_region = region;

    // Use the all regions endpoint without specifying a manager
    // This will get data for all regions and all BHs combined
    const response = await api.post('/dashboard/region-hierarchy/products', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching all products hierarchy:', error);
    throw error;
  }
};

export interface ChannelHierarchy {
  name: string;
  metrics: Metrics;
  subchannels?: { [key: string]: ChannelHierarchy };
}

export const fetchAllChannelsHierarchy = async (
  filters?: ProductHierarchyFilters,
  region?: string
): Promise<{ [key: string]: ChannelHierarchy }> => {
  try {
    const payload: Record<string, any> = {};
    
    if (filters?.selected_months) payload.selected_months = filters.selected_months;
    if (filters?.selected_years) payload.selected_years = filters.selected_years;
    if (filters?.selected_lob) payload.selected_lob = filters.selected_lob;
    if (filters?.selected_products) {
      // Convert ProductInfo objects to their mis_product values
      payload.selected_products = filters.selected_products.map(product => 
        typeof product === 'string' ? product : product.mis_product
      );
    }
    if (filters?.selected_channels) payload.selected_channels = filters.selected_channels;
    if (filters?.selected_sub_channels) payload.selected_sub_channels = filters.selected_sub_channels;
    if (filters?.selected_region) payload.selected_region = filters.selected_region;
    
    // Also accept region as a separate parameter for backward compatibility
    if (region && region !== 'all') payload.selected_region = region;

    // Use the all regions endpoint without specifying a manager
    // This will get data for all regions and all BHs combined
    const response = await api.post('/dashboard/region-hierarchy/channels', payload);
    return response.data;
  } catch (error) {
    console.error('Error fetching all channels hierarchy:', error);
    throw error;
  }
};

export interface OverallPerformance {
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface LOBPerformance {
  lob: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

// Helper function to build URL parameters from filter objects
const buildFilterParams = async (params: URLSearchParams, filters?: SelectedFilters) => {
  // Get caller function name from stack trace
  const stackTrace = new Error().stack;
  const callerName = stackTrace?.split('\n')[2]?.trim()?.split(' ')[1] || 'unknown';
  
  if (filters) {
    if (filters.selectedMonth) {
      params.append('month', filters.selectedMonth);
    }

    if (filters.selectedYear) {
      try {
        const options = cachedFilterOptions || await fetchFilterOptions();
        const selectedYearValue = Array.isArray(filters.selectedYear)
          ? filters.selectedYear[0]
          : filters.selectedYear;
    
        // Try to find matching year option from cached options
        let yearOption = options?.years?.find(y => y.id === selectedYearValue);
    
        // If not found, create year option manually
        if (!yearOption && /^\d{4}$/.test(selectedYearValue)) {
          yearOption = {
            id: `${selectedYearValue}_${Number(selectedYearValue) + 1}`,
            name: `FY ${selectedYearValue}-${(Number(selectedYearValue) + 1).toString().slice(-2)}`,
            start_year: Number(selectedYearValue),
            end_year: Number(selectedYearValue) + 1
          };
        }
    
        
        if (yearOption && filters.selectedMonth) {
          const selectedMonth = parseInt(filters.selectedMonth);
          const calendarYear =
            selectedMonth >= 1 && selectedMonth <= 3
              ? yearOption.end_year.toString()
              : yearOption.start_year.toString();
    
          params.append('year', calendarYear);
        } else if (yearOption) {
          params.append('year', yearOption.id);
        }
      } catch (error) {
        console.error('Error getting year details:', error);
        const fallbackYear = Array.isArray(filters.selectedYear)
          ? filters.selectedYear[0]
          : filters.selectedYear;
        params.append('year', fallbackYear);
      }
    }

    
    if (filters.selectedLOB?.length) {
      filters.selectedLOB.forEach(lob => params.append('lob', lob));
    }
    if (filters.selectedProducts?.length) {
      filters.selectedProducts.forEach(product => params.append('product', product));
    }
    if (filters.selectedChannels?.length) {
      filters.selectedChannels.forEach(channel => params.append('channel_id', channel));
    }
    if (filters.selectedSubChannels?.length) {
      filters.selectedSubChannels.forEach(subChannel => params.append('subchannel_id', subChannel));
    }
    if (filters.selectedSubProducts?.length) {
      filters.selectedSubProducts.forEach(subProduct => params.append('sub_product', subProduct));
    }
    if (filters.selectedRegion) {
      params.append('region_id', filters.selectedRegion);
    }
    if (filters.dateRange?.startDate) {
      params.append('start_date', filters.dateRange.startDate.toISOString().split('T')[0]);
    }
    if (filters.dateRange?.endDate) {
      params.append('end_date', filters.dateRange.endDate.toISOString().split('T')[0]);
    }
  }
  return params;
};

export const fetchOverallPerformance = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<OverallPerformance> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('overall', paramsString)) {
      return performanceCache['overall']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/overall?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('overall', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching overall performance:', error);
    throw error;
  }
};

export const fetchLOBPerformance = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<LOBPerformance[]> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('lob', paramsString)) {
      return performanceCache['lob']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/lob?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('lob', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching LOB performance:', error);
    throw error;
  }
};

export const fetchRegionMap = async (
  filters?: SelectedFilters
): Promise<RegionMapResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', 'true');
    
    // Add filter parameters if provided
    if (filters) {
      // Create a copy of filters without selectedRegion
      const filtersWithoutRegion = { ...filters };
      delete filtersWithoutRegion.selectedRegion;
      
      await buildFilterParams(params, filtersWithoutRegion);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('regionMap', paramsString)) {
      return performanceCache['regionMap']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/region-map?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('regionMap', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching region map data:', error);
    throw error;
  }
};

export const fetchProductChannelDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ProductChannelResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('productChannel', paramsString)) {
      return performanceCache['productChannel']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/product-channel?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('productChannel', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product channel distribution:', error);
    throw error;
  }
};

export const fetchChannelProductDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ChannelProductResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelProduct', paramsString)) {
      return performanceCache['channelProduct']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/channel-product?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelProduct', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel product distribution:', error);
    throw error;
  }
};

export const fetchProductStatusDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ProductStatusResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('productStatus', paramsString)) {
      return performanceCache['productStatus']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/product-status?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('productStatus', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product status distribution:', error);
    throw error;
  }
};

export const fetchChannelStatusDistribution = async (
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ChannelStatusResponse> => {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelStatus', paramsString)) {
      return performanceCache['channelStatus']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/distribution/channel-status?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelStatus', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel status distribution:', error);
    throw error;
  }
};

export async function fetchProductConversionRate(
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ProductConversionResponse> {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('productConversion', paramsString)) {
      return performanceCache['productConversion']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/conversion-rate/product?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('productConversion', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching product conversion rate:', error);
    throw error;
  }
}

export async function fetchChannelConversionRate(
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<ChannelConversionResponse> {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelConversion', paramsString)) {
      return performanceCache['channelConversion']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/conversion-rate/channel?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelConversion', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel conversion rate:', error);
    throw error;
  }
}

export async function fetchSubchannelConversionRate(
  currentMonthOnly: boolean = true,
  filters?: SelectedFilters
): Promise<SubchannelConversionResponse> {
  try {
    const params = new URLSearchParams();
    params.append('current_month_only', currentMonthOnly.toString());
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('subchannelConversion', paramsString)) {
      return performanceCache['subchannelConversion']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/conversion-rate/subchannel?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('subchannelConversion', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching subchannel conversion rate:', error);
    throw error;
  }
}

export async function fetchChannelPerformanceBreakdown(
  filters?: SelectedFilters
): Promise<ChannelPerformanceBreakdownResponse> {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('channelBreakdown', paramsString)) {
      return performanceCache['channelBreakdown']!.data;
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/channel-breakdown?${paramsString}`;
    const response = await api.get(url);
    
    // Cache the response
    updateCache('channelBreakdown', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching channel performance breakdown:', error);
    throw error;
  }
}

export async function fetchSubchannelPerformanceBreakdown(
  filters?: SelectedFilters
): Promise<SubchannelPerformanceResponse> {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/subchannel-breakdown?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching subchannel performance breakdown:', error);
    throw error;
  }
}

export async function fetchProductPerformanceBreakdown(
  filters?: SelectedFilters
): Promise<ProductPerformanceResponse> {
  try {
    const params = new URLSearchParams();
    
    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const url = `${API_BASE_URL}/dashboard/performance/product-breakdown?${params.toString()}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching product performance breakdown:', error);
    throw error;
  }
}

export interface DependentFilterOptions {
  channels: string[];
  subchannels: string[];
  product_groups: string[];
  lobs: string[];
}

export const fetchDependentFilters = async (
  channel?: string | string[],
  subchannel?: string | string[],
  product_group?: string | string[],
  sub_product?: string | string[],
  lob?: string | string[],
  year?: string
): Promise<FilterOptions> => {
  const params = new URLSearchParams();
  
  if (channel) {
    if (Array.isArray(channel)) {
      channel.forEach(c => params.append('channel_id', c));
    } else {
      params.append('channel_id', channel);
    }
  }
  
  if (subchannel) {
    if (Array.isArray(subchannel)) {
      subchannel.forEach(sc => params.append('subchannel_id', sc));
    } else {
      params.append('subchannel_id', subchannel);
    }
  }
  
  if (product_group) {
    if (Array.isArray(product_group)) {
      product_group.forEach(pg => params.append('product', pg));
    } else {
      params.append('product', product_group);
    }
  }

  if (sub_product) {  
    if (Array.isArray(sub_product)) {
      sub_product.forEach(sp => params.append('sub_product', sp));
    } else {
      params.append('sub_product', sub_product);
    }
  }
  if (lob) {
    if (Array.isArray(lob)) {
      lob.forEach(l => params.append('lob', l));
    } else {
      params.append('lob', lob);
    }
  }
  
  if (year) params.append('year', year);

  const response = await api.get(`/dashboard/filters/dependent?${params.toString()}`);
  return {
    months: response.data.months,
    years: response.data.years,
    channel: response.data.channel,
    subchannel: response.data.subchannel,
    product_group: response.data.product_group,
    sub_product: response.data.sub_product || [],
    lob: response.data.lob
  };
};

// Interface for RegionMap response
export interface RegionMapResponse {
  region_data: {
    [key: string]: {
      total_leads: string;
      converted: string;
      conversion_rate: string;
    };
  };
}

// Interface for ProductChannel response
export interface ProductChannelData {
  product_group: string;
  total_leads: string;
  converted_leads: string;
  channels: {
    [key: string]: {
      total_leads: string;
      converted_leads: string;
      conversion_rate: string;
      contribution_rate: string;
    };
  };
  conversion_rate: string;
}

export interface ProductChannelResponse {
  products: ProductChannelData[];
}

// Interface for ChannelProduct response
export interface ChannelProductData {
  channel: string;
  total_leads: string;
  converted_leads: string;
  products: {
    [key: string]: {
      product_group: string;
      total_leads: string;
      converted_leads: string;
      conversion_rate: string;
      contribution_rate: string;
    };
  };
  conversion_rate: string;
}

export interface ChannelProductResponse {
  channels: ChannelProductData[];
}

// Interface for ProductStatus response
export interface ProductStatusData {
  product_group: string;
  total_leads: string;
  converted_leads: string;
  statuses: {
    [key: string]: {
      status: string;
      total_leads: string;
      percentage: string;
      conversion_rate: string;
      converted_leads: string;
    };
  };
}

export interface ProductStatusResponse {
  products: ProductStatusData[];
}

// Interface for ChannelStatus response
export interface ChannelStatusData {
  channel: string;
  total_leads: string;
  statuses: {
    [key: string]: {
      status: string;
      total_leads: string;
      percentage: string;
      conversion_rate: string;
      converted_leads: string;
    };
  };
}

export interface ChannelStatusResponse {
  channels: ChannelStatusData[];
}

// Interface for SubchannelStatus response
export interface SubchannelStatusData {
  subchannel: string;
  total_leads: string;
  statuses: {
    [key: string]: {
      status: string;
      total_leads: string;
      percentage: string;
      conversion_rate: string;
      converted_leads: string;
    };
  };
}

export interface SubchannelStatusResponse {
  subchannels: SubchannelStatusData[];
}

// Interface for ProductConversion response
export interface ProductConversionData {
  product_group: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface ProductConversionResponse {
  products: ProductConversionData[];
}

// Interface for ChannelConversion response
export interface ChannelConversionData {
  channel: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface ChannelConversionResponse {
  channels: ChannelConversionData[];
}

// Interface for SubchannelConversion response
export interface SubchannelConversionData {
  subchannel: string;
  channel: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

export interface SubchannelConversionResponse {
  subchannels: SubchannelConversionData[];
}

// Interface for ChannelPerformanceBreakdown
export interface PerformanceMetrics {
  total_leads: string;
  converted: string;
  conversion_rate: string;
}

export interface SubProductItem {
  sub_product: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  target?: number;
}

export interface ProductItem {
  product: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  sub_products: SubProductItem[];
  target?: number;
}

export interface LOBItem {
  lob: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  products: ProductItem[];
  target?: number;
}

export interface SubchannelItem {
  subchannel: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  lobs?: LOBItem[];
  target?: number;
}

export interface ChannelBreakdown {
  channel: string;
  ftm: PerformanceMetrics;
  aod: PerformanceMetrics;
  sub_channels: SubchannelItem[];
  target?: number;
}

export interface ChannelPerformanceBreakdownResponse {
  channels: ChannelBreakdown[];
}

// Interface for SubchannelPerformance response
export interface SubchannelPerformanceItem {
  subchannel: string;
  channel: string;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
}

export interface SubchannelPerformanceResponse {
  subchannels: SubchannelPerformanceItem[];
}

// Interface for ProductPerformance response
export interface SubProductItem {
  sub_product: string;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
}

export interface ProductItem {
  product_name: string;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  sub_products: SubProductItem[];
}

export interface ProductGroupItem {
  product_group: string | null;
  ftm: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  aod: {
    total_leads: string;
    converted: string;
    conversion_rate: string;
  };
  products: ProductItem[];
}

export interface ProductPerformanceResponse {
  products: ProductGroupItem[];
}

// Manager Breakdown API
export interface ManagerBreakdownSubProduct {
  sub_product: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
}

export interface ManagerBreakdownProduct {
  product_name: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  sub_products: ManagerBreakdownSubProduct[];
}

export interface ManagerBreakdownProductGroup {
  product_group: string | null;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  products: ManagerBreakdownProduct[];
}

export interface ManagerBreakdownSubChannel {
  subchannel: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
}

export interface ManagerBreakdownChannel {
  channel: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  sub_channels: ManagerBreakdownSubChannel[];
}

export interface ManagerBreakdownSubordinate {
  employee_id: string;
  employee_name: string;
  employee_role: string;
  role_code: string;
  hierarchy_path: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
}

export interface ManagerBreakdownResponse {
  employee_id: string;
  employee_name: string;
  employee_role: string;
  ftm: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  aod: {
    total_leads: string | number;
    converted: string | number;
    conversion_rate: string | number;
  };
  products: ManagerBreakdownProductGroup[];
  channels: ManagerBreakdownChannel[];
  subordinates: ManagerBreakdownSubordinate[];
}

export const fetchManagerBreakdown = async (
  employeeId?: string,
  role?: string,
  hierarchyPath?: string,
  filters?: SelectedFilters
): Promise<ManagerBreakdownResponse> => {
  try {
    const params = new URLSearchParams();
    // Only append employee_id if it's defined and not empty
    if (employeeId !== undefined && employeeId !== '') {
      params.append('target_employee_id', employeeId);
    }
    if (role !== undefined && role !== '') {
      params.append('role', role);
    }
    if (hierarchyPath !== undefined && hierarchyPath !== '') {
      params.append('hierarchy_path', hierarchyPath);
    }

    // Add filter parameters if provided
    if (filters) {
      await buildFilterParams(params, filters);
    }
    
    const paramsString = params.toString();
    
    // Check if we have valid cached data for this specific request
    if (isValidCache('managerBreakdown', paramsString)) {
      return performanceCache['managerBreakdown']!.data;
    }

    
    const url = `${API_BASE_URL}/dashboard/performance/manager-breakdown?${paramsString}`;
    
    const response = await api.get(url);
    
    // Cache the response
    updateCache('managerBreakdown', response.data, paramsString);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching manager breakdown:', error);
    throw error;
  }
}; 
